import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { PricingTableFormValues } from "@/schemas/pricingTableSchema";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Trash2, Plus, Users, HeartHandshake, Clock, DollarSign } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PricingTableTransactionFeesProps {
  feesApportionParties: any[];
}

export const PricingTableTransactionFees: React.FC<PricingTableTransactionFeesProps> = ({
  feesApportionParties
}) => {
  const { control, watch, setValue } = useFormContext<PricingTableFormValues>();
  
  const {
    fields: transactionFeeFields,
    append: appendTransactionFee,
    remove: removeTransactionFee
  } = useFieldArray({
    control,
    name: "transaction_fees"
  });
  
  const handleAddTransactionFee = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Adding new transaction fee");
    
    const newFeeId = transactionFeeFields.length > 0 
      ? Math.max(...transactionFeeFields.map(fee => fee.fee_id || 0)) + 1 
      : 1;
    
    const defaultAmount = 0;
    const defaultApportions = feesApportionParties.map(party => ({
      apportionment_party_id: party.id,
      amount: defaultAmount
    }));
    
    appendTransactionFee({
      fee_id: newFeeId,
      name: `Transaction Fee ${newFeeId}`,
      multiply_by_applicants: false,
      married_count_as_one: false,
      amount: 0,
      timing: 'upfront',
      apportionment: defaultApportions
    });
  };
  
  const getApportionmentValue = (apportionments: any[] = [], partyId: number) => {
    const apportionment = apportionments.find(a => a.apportionment_party_id === partyId);
    return apportionment ? `$${apportionment.amount.toFixed(2)}` : '$0.00';
  };
  const updateApportionmentAmount = (feeIndex: number, partyId: number, value: string) => {
    const numValue = value === '' ? 0 : parseFloat(value);
    
    const currentApportionments = [...(watch(`transaction_fees.${feeIndex}.apportionment`) || [])];
    
    const apportionmentIndex = currentApportionments.findIndex(a => 
      a.apportionment_party_id === partyId
    );
    
    if (apportionmentIndex >= 0) {
      const updatedApportionments = [...currentApportionments];
      updatedApportionments[apportionmentIndex] = {
        ...updatedApportionments[apportionmentIndex],
        amount: numValue
      };
      setValue(`transaction_fees.${feeIndex}.apportionment`, updatedApportionments);
    } else {
      const newApportionment = [
        ...currentApportionments, 
        {
          apportionment_party_id: partyId,
          amount: numValue
        }
      ];
      setValue(`transaction_fees.${feeIndex}.apportionment`, newApportionment);
    }
    
    setTimeout(() => {
      validateRowTotal(feeIndex);
    }, 100);
  };

  const validateRowTotal = (feeIndex: number) => {
    const apportionments = watch(`transaction_fees.${feeIndex}.apportionment`) || [];
    const feeAmount = watch(`transaction_fees.${feeIndex}.amount`) || 0;
    const total = apportionments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    
    if (Math.abs(total - feeAmount) > 0.01) {
      toast.warning(`Fee "${watch(`transaction_fees.${feeIndex}.name`)}" apportionments total should equal the fee amount of $${feeAmount.toFixed(2)}`);
    }
  };

  const getApportionmentAmount = (apportionments: any[] = [], partyId: number) => {
    const apportionment = apportionments.find(a => a.apportionment_party_id === partyId);
    return apportionment ? apportionment.amount || 0 : 0;
  };

  return (
    <TooltipProvider>
      <div className="rounded-md border shadow-sm p-4 bg-white">
        <div className="flex justify-between items-center mb-4">
          <Button 
            type="button" 
            variant="outline" 
            size="sm" 
            onClick={handleAddTransactionFee}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Fee
          </Button>
        </div>
        
        {transactionFeeFields.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No transaction fees defined
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Amount</TableHead>
                                      <TableHead className="w-32">When</TableHead>
                  <TableHead className="w-20 text-center">Options</TableHead>
                  {feesApportionParties.map(party => (
                    <TableHead key={party.id}>
                      {party.description || party.entity_name}
                    </TableHead>
                  ))}
                  <TableHead className="w-16">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactionFeeFields.map((field, index) => (
                  <TableRow key={field.id}>
                    <TableCell>
                      <FormField
                        control={control}
                        name={`transaction_fees.${index}.name`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input 
                                placeholder="Fee Name" 
                                value={field.value || ""}
                                onChange={field.onChange}
                                className="h-8 min-w-[150px]"
                                type="text"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={control}
                        name={`transaction_fees.${index}.amount`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <div className="flex items-center justify-center space-x-0.5">
                                <span className="text-gray-500">$</span>
                                <Input 
                                  type="number"
                                  min="0"
                                  placeholder="0.00"
                                  className="amount-input"
                                  style={{ appearance: "textfield" }}
                                  value={field.value === undefined || field.value === null ? 0 : field.value}
                                  onChange={(e) => {
                                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                    field.onChange(isNaN(value) ? 0 : value);
                                  }}
                                />
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <FormField
                        control={control}
                        name={`transaction_fees.${index}.timing`}
                        render={({ field }) => {
                          console.log(`Transaction fee ${index} timing field value:`, field.value);
                          return (
                            <FormItem>
                              <FormControl>
                                <Select
                                  value={field.value || 'upfront'}
                                  onValueChange={(value) => {
                                    console.log(`Transaction fee ${index} timing changed to:`, value);
                                    field.onChange(value);
                                  }}
                                >
                                <SelectTrigger className="h-8 min-w-[100px]">
                                  <SelectValue placeholder="Select timing">
                                    <div className="flex items-center gap-1 whitespace-nowrap">
                                      {field.value === 'at_closing' ? (
                                        <>
                                          <Clock className="h-3 w-3 text-orange-500 flex-shrink-0" />
                                          <span className="text-xs">At Closing</span>
                                        </>
                                      ) : (
                                        <>
                                          <DollarSign className="h-3 w-3 text-green-500 flex-shrink-0" />
                                          <span className="text-xs">Upfront</span>
                                        </>
                                      )}
                                    </div>
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="upfront">
                                    <div className="flex items-center gap-1 whitespace-nowrap">
                                      <DollarSign className="h-3 w-3 text-green-500 flex-shrink-0" />
                                      <span className="text-xs">Upfront</span>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="at_closing">
                                    <div className="flex items-center gap-1 whitespace-nowrap">
                                      <Clock className="h-3 w-3 text-orange-500 flex-shrink-0" />
                                      <span className="text-xs">At Closing</span>
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                          </FormItem>
                          );
                        }}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center justify-center space-x-1">
                        <FormField
                          control={control}
                          name={`transaction_fees.${index}.multiply_by_applicants`}
                          render={({ field }) => (
                            <FormItem className="flex flex-col items-center space-y-1">
                              <FormControl>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex flex-col items-center">
                                      <Switch
                                        checked={field.value || false}
                                        onCheckedChange={field.onChange}
                                      />
                                      <Users 
                                        size={14} 
                                        className={field.value ? "text-blue-500" : "text-gray-300"} 
                                      />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Multiply by Applicants</p>
                                  </TooltipContent>
                                </Tooltip>
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={control}
                          name={`transaction_fees.${index}.married_count_as_one`}
                          render={({ field }) => (
                            <FormItem className="flex flex-col items-center space-y-1">
                              <FormControl>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex flex-col items-center">
                                      <Switch
                                        checked={field.value || false}
                                        onCheckedChange={field.onChange}
                                      />
                                      <HeartHandshake
                                        size={14} 
                                        className={field.value ? "text-purple-500" : "text-gray-300"} 
                                      />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Married Count as One</p>
                                  </TooltipContent>
                                </Tooltip>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </TableCell>
                    
                    {feesApportionParties.map(party => (
                      <TableCell key={party.id}>
                        <div className="flex items-center justify-center space-x-0.5">
                          <span className="text-gray-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            className="apportion-input"
                            style={{ appearance: "textfield" }}
                            value={getApportionmentAmount(watch(`transaction_fees.${index}.apportionment`), party.id)}
                            onChange={(e) => updateApportionmentAmount(index, party.id, e.target.value)}
                          />
                        </div>
                      </TableCell>
                    ))}
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log("Removing transaction fee at index", index);
                            removeTransactionFee(index);
                          }}
                          className="text-red-500 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Remove fee</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
